import React from 'react';
import { Box, Typography } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';

// Import Sample Data Badge
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Gender Stats Donut Chart for MBF Dashboard
 * Displays male/female distribution with callout labels
 * 
 * @param {number} maleCount - Number of male users
 * @param {number} femaleCount - Number of female users
 */
const MBFGenderStatsChart = ({ maleCount = 50, femaleCount = 100, isSampleData = false }) => {
    const total = maleCount + femaleCount;
    const malePercentage = total > 0 ? (maleCount / total).toFixed(1) : 0;
    const femalePercentage = total > 0 ? (femaleCount / total).toFixed(1) : 0;

    const data = {
        labels: ['Male', 'Female'],
        datasets: [
            {
                data: [maleCount, femaleCount],
                backgroundColor: ['#8ccbff', '#ffb3cf'],
                borderWidth: 0,
                cutout: '65%',
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // We'll create custom legend
            },
            tooltip: {
                backgroundColor: '#1a1a1a',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                padding: 12,
                borderRadius: 8,
                callbacks: {
                    label: (context) => {
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.label}: ${value} (${percentage}%)`;
                    },
                },
            },
        },
    };

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '24px',
                border: '1px solid #e6e6e6',
                padding: '12px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '25px',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
            }}
        >
            {/* Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: 'stretch' }}>
                <Typography
                    sx={{
                        color: '#4d4d4d',
                        fontFamily: '"Roboto", sans-serif',
                        fontSize: '16px',
                        lineHeight: '20px',
                        fontWeight: 600,
                    }}
                >
                    Gender stats
                </Typography>
                {isSampleData && <SampleDataBadge absolute={false} />}
            </Box>

            {/* Chart Container */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '25px',
                    alignItems: 'center',
                }}
            >
                {/* Donut Chart with Labels */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '171px',
                        height: '114px',
                    }}
                >
                    {/* Chart */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '28px',
                            top: 0,
                            width: '114px',
                            height: '114px',
                        }}
                    >
                        <Doughnut data={data} options={options} />
                    </Box>

                    {/* Female Label (top right) - 01 */}
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: '2px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {/* Callout line */}
                        <svg width="33" height="20" viewBox="0 0 33 20" fill="none">
                            <path d="M0 18 L20 18 L25 2 L33 2" stroke="#ff448c" strokeWidth="1" fill="none" />
                            <circle cx="0" cy="18" r="3" fill="#ff448c" />
                        </svg>
                        <Typography
                            sx={{
                                color: '#ff448c',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '12px',
                                fontWeight: 600,
                                lineHeight: '120%',
                                ml: 0.5,
                            }}
                        >
                            {femalePercentage}
                        </Typography>
                    </Box>

                    {/* Male Label (bottom left) - 0.5 */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            bottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#0576d3',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '12px',
                                fontWeight: 600,
                                lineHeight: '120%',
                                mr: 0.5,
                            }}
                        >
                            {malePercentage}
                        </Typography>
                        {/* Callout line */}
                        <svg width="25" height="16" viewBox="0 0 25 16" fill="none">
                            <path d="M0 8 L10 8 L15 14 L25 14" stroke="#0576d3" strokeWidth="1" fill="none" />
                            <circle cx="25" cy="14" r="3" fill="#0576d3" />
                        </svg>
                    </Box>
                </Box>

                {/* Custom Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '16px',
                        alignItems: 'center',
                    }}
                >
                    {/* Male Legend */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <Box
                            sx={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                bgcolor: '#8ccbff',
                            }}
                        />
                        <Typography
                            sx={{
                                color: '#666666',
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '12px',
                                lineHeight: '16px',
                                fontWeight: 400,
                            }}
                        >
                            Male
                        </Typography>
                    </Box>

                    {/* Female Legend */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <Box
                            sx={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                bgcolor: '#ffb3cf',
                            }}
                        />
                        <Typography
                            sx={{
                                color: '#666666',
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '12px',
                                lineHeight: '16px',
                                fontWeight: 400,
                            }}
                        >
                            Female
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFGenderStatsChart;
